import { XMarkIcon } from '@heroicons/react/24/outline';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import type { RootState } from '../../../../store/store';
import { JWTHeader } from '../../../../utils/JWTHeader';
import Spinner from '../../../common/Spinner';

interface Company {
  id: number;
  name: string;
  siren: string;
}

interface CompanySearchProps {
  onCompanySelect: (company: Company | null) => void;
  placeholder?: string;
  className?: string;
  showClearButton?: boolean;
  resetKey?: number;
  initialCompany?: Company | null;
}

const CompanySearch: React.FC<CompanySearchProps> = ({
  onCompanySelect,
  placeholder = 'Rechercher une entreprise...',
  className = '',
  showClearButton = false,
  resetKey = 0,
  initialCompany = null,
}) => {
  const [searchQuery, setSearchQuery] = useState(
    initialCompany ? `${initialCompany.name} (${initialCompany.siren})` : '',
  );
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(initialCompany);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Reset component when resetKey changes
  useEffect(() => {
    if (resetKey > 0) {
      setSearchQuery('');
      setCompanies([]);
      setShowDropdown(false);
      setSelectedCompany(null);
    }
  }, [resetKey]);

  // Handle initialCompany prop changes
  useEffect(() => {
    if (initialCompany) {
      setSelectedCompany(initialCompany);
      setSearchQuery(`${initialCompany.name} (${initialCompany.siren})`);
    }
  }, [initialCompany]);

  const fetchCompanies = async() => {
    setLoading(true);
    try {
      const response = await fetch(`/api/companies?step=1&limit=10&filterSearch=${searchQuery}`, JWTHeader(jwt));
      if (response.ok) {
        const data = await response.json();
        setCompanies(data.results);
        setShowDropdown(true);
      } else {
        console.error('Failed to fetch companies');
      }
    } catch (error) {
      console.error('Error fetching companies :', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchQuery.length > 2 && !selectedCompany) {
      const debounceTimer = setTimeout(() => {
        fetchCompanies();
      }, 300);

      return () => clearTimeout(debounceTimer);
    }
    setCompanies([]);
    setShowDropdown(false);
  }, [searchQuery, selectedCompany]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setSearchQuery(value);
    setSelectedCompany(null);
  };

  const handleSelect = (company: Company) => {
    setSelectedCompany(company);
    onCompanySelect(company);
    setSearchQuery(`${company.name} (${company.siren})`);
    setShowDropdown(false);
    if (inputRef.current) {
      inputRef.current.blur();
    }
  };

  const handleClear = () => {
    setSelectedCompany(null);
    setSearchQuery('');
    setCompanies([]);
    setShowDropdown(false);
    onCompanySelect(null);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className={`relative ${className}`} ref={containerRef}>
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        value={searchQuery}
        onChange={handleInputChange}
          onFocus={() => !selectedCompany && searchQuery.length > 2 && setShowDropdown(true)}
          placeholder={placeholder}
          className={`w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-rgpd-blue focus:ring-rgpd-blue ${
            showClearButton && selectedCompany ? 'pr-8' : ''
          }`}
        autoComplete="nope"
      />
      {loading && <Spinner size="sm" className="absolute top-2 right-2" />}
        {showClearButton && selectedCompany && !loading && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute top-1/2 right-2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            title="Effacer la sélection"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>
      {showDropdown && !selectedCompany && companies.length > 0 && (
        <ul className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {companies.map((company) => (
            <li
              key={company.id}
              onClick={() => handleSelect(company)}
              className="cursor-pointer px-3 py-2 text-sm hover:bg-gray-100"
            >
              <span className="font-medium text-gray-900">{company.name}</span>
              <span className="ml-1 text-gray-500">({company.siren})</span>
            </li>
          ))}
        </ul>
      )}
      {showDropdown && !selectedCompany && searchQuery.length > 2 && !loading && companies.length === 0 && (
        <div className="absolute z-50 mt-1 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-500 shadow-lg">
          Aucune entreprise trouvée
        </div>
      )}
    </div>
  );
};

export default CompanySearch;
